import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { KTSVG, toAbsoluteUrl } from "../../../_metronic/helpers";
import moment from "moment";

interface BookingData {
  id: number;
  items: string;
  booking_id: string;
  consignor: string;
  consignor_address: string;
  consignee: string;
  consignee_address: string;
  payment_mode: string;
  shipment_mode: string;
  notes: string;
  invoice_value: string;
  nrml_payment_quantity: string;
  created_at: string;
  description: string;
  booking_mode: string;
  total_quantity: number;
  large_box_quantity: number;
  medium_box_quantity: number;
  small_box_quantity: number;
  originating_hub: string;
  destination_hub: string;
  country: string;
}

interface LocationState {
  booking_id: string;
  // data: BookingData;
}

const BookingPrint: React.FC = () => {
  const pdfRef = useRef<HTMLDivElement>(null);
  const location = useLocation<LocationState>();
  const { booking_id } = location.state || { booking_id: "" };
  // const { data } = location.state || { data: {} as BookingData };
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [storedName, setStoredName] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);

  useEffect(() => {
    const storedData = localStorage.getItem("Data");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setStoredName(parsedData.name);
    }
  }, []);

  // useEffect(() => {
  // if (data?.booking_id) {
  // setBookingId(data.booking_id);
  // }
  // }, [data]);

  // Fetch booking data when component mounts
  useEffect(() => {
    const fetchBookingData = async () => {
      if (!booking_id) {
        setError("No booking ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          "https://backendcway.causewaycargo.com/superadmin/viewparticularbooking",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              auth: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do",
            },
            body: JSON.stringify({ booking_id }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();

        if (result.status === "success") {
          setBookingData(result.data[0]);
          setError(null);
        } else {
          throw new Error("Failed to fetch booking data");
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(`Error: ${err.message}`);
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [booking_id]);

  const generatePdf = async () => {
    const input = pdfRef.current;
    if (!input) return;

    try {
      // Get the selected paper size from user preference or use A4 as default
      // TypeScript-safe DOM querying
      const paperSizeElement = document.querySelector(
        'select[aria-label="Paper size"]'
      ) as HTMLSelectElement | null;
      const paperFormat = paperSizeElement?.value || "a5";

      // Define dimensions for various paper formats (width/height in pixels at 96 DPI)
      const paperDimensions: Record<string, { width: number; height: number }> =
        {
          a4: { width: 794, height: 1123 },
          a5: { width: 559, height: 794 },
          letter: { width: 816, height: 1056 },
          legal: { width: 816, height: 1344 },
        };

      // Get selected dimensions or default to A4
      // TypeScript-safe indexing
      const paperFormatKey =
        paperFormat.toLowerCase() as keyof typeof paperDimensions;
      const selectedPaper =
        paperDimensions[paperFormatKey] || paperDimensions.a5;

      // Calculate optimal scale based on content and paper size
      const contentWidth = input.offsetWidth;
      const contentHeight = input.offsetHeight;
      const scaleX = selectedPaper.width / contentWidth;
      const scaleY = selectedPaper.height / contentHeight;

      // Use the smaller scale to ensure content fits within page
      const scale = Math.min(scaleX, scaleY) * 0.95; // 0.95 to add a small margin

      // Capture HTML to canvas with appropriate scaling
      const canvas = await html2canvas(input, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
        scale: Math.min(scale * 2, 3),
        windowWidth: contentWidth,
      });

      const imgData = canvas.toDataURL("image/png");
      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        alert("Popup blocked! Please allow popups for this website.");
        return;
      }

      // Determine orientation
      const isLandscape = selectedPaper.width > selectedPaper.height;
      const orientation = isLandscape && "landscape";

      // TypeScript-safe access to booking_id
      const bookingIdDisplay = bookingData?.booking_id || "";

      printWindow.document.write(`
 <!DOCTYPE html>
 <html>
 <head>
 <title>Consignment Note - ${bookingIdDisplay}</title>
 <style>
 @page {
 margin: 0;
 size: ${paperFormat} ${orientation};
 }
 body, html {
 margin: 0;
 padding: 0;
 text-align: center;
 width: 100%;
 height: 100%;
 display: flex;
 justify-content: center;
 align-items: center;
 }
 .print-container {
 width: 100%;
 height: 100%;
 display: flex;
 justify-content: center;
 align-items: center;
 overflow: hidden;
 }
 img {
 max-width: 100%;
 max-height: auto;
 object-fit: contain;
 page-break-inside: avoid;
 }
 @media print {
 body {
 width: 100%;
 height: 100%;
 }
 img {
 max-width: 100%;
 max-height: 100%;
 width: auto;
 height: auto;
 }
 .print-container {
 page-break-inside: avoid;
 }
 }
</style>

 </head>
 <body>
 <div class="print-container">
 <img src="${imgData}" />
 </div>
 <script>
 // Short delay before showing print dialog
 setTimeout(function() {
 window.print();
 // Optional: Close window after printing
 // window.addEventListener('afterprint', function() { window.close(); });
 }, 1000); // Give more time for larger images to render
 </script>
 </body>
 </html>
 `);

      printWindow.document.close();
    } catch (error) {
      console.error("Error generating print preview:", error);
      alert("Failed to generate print preview. Please try again.");
    }
  };

  // Now, let's update the CSS to make the layout more responsive to different page sizes
  const styles = `
 .consignment-table {
 width: 100%;
 border-collapse: collapse;
 table-layout: fixed; 
 }
 
 .consignment-table td, .consignment-table th {
 border: 1px solid #000;
 padding: 6px 8px;
 word-wrap: break-word; 
 overflow: hidden;
 font-size: 14px; 
 }
 
 .consignment-title {
 font-size: clamp(16px, 5vw, 18px);
 font-weight: bold;
 text-align: center;
 padding: 8px 0;
 }
 
 .section-header {
 font-size: clamp(12px, 4vw, 14px); 
 font-weight: bold;
 text-align: center;
 padding: 6px 0;
 }
 
 .field-label {
 font-size: clamp(12px, 3.5vw, 14px);
 font-weight: bold;
 white-space: nowrap;
 }
 
 .field-value {
 font-size: clamp(12px, 4vw, 16px);
 font-weight: 500;
 }
 
 .footer-text {
 background-color: #dc3545;
 color: white;
 font-size: clamp(10px, 3vw, 12px); 
 padding: 5px 8px;
 width: 100%;
 }
 
 .company-logo {
 max-width: 250px;
 width: 100%;
 height: auto;
 }
 
 .pdf-container {
 width: 100%;
 max-width: 1300px; 
 margin: 0 auto;
 padding: 20px;
 box-sizing: border-box;
 }

 .company-name-ar {
 font-size: clamp(22px, 6vw, 36px);
 color: #dc3545;
 font-weight: bold;
 line-height: 1.2;
 }
 
 .company-name-en {
 font-size: clamp(18px, 5vw, 28px);
 font-weight: bold;
 line-height: 1.2;
 margin-top: 4px;
 }
 
 .company-address {
 font-size: clamp(11px, 3vw, 13px);
 margin-top: 2px;
 line-height: 1.3;
 }
 
 .contact-info {
 font-size: clamp(11px, 3vw, 14px);
 font-weight: bold;
 }
 
 .signature-area {
 margin-top: 15px;
 width: 100%;
 }
 `;

  // Alternative approach if the user can't select paper size from the UI
  // We'll use this function to get the paper size directly from the print preferences
  // This won't work in all browsers due to security restrictions

  // Render loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center pt-15">
        Loading booking bookingData...
      </div>
    );
  }

  // Render error state
  if (error) {
    return <div className="alert alert-danger m-5">{error}</div>;
  }

  // Render if no data is available
  if (!bookingData) {
    return <div className="alert alert-warning m-5 text-center py-4 text-gray-500">🚫 No booking data available</div>;
  }

  return (
    <>
      <style>{styles}</style>
      <div ref={pdfRef} className="pdf-container bg-white rounded-3 py-3">
        <div className="row d-flex justify-content-between align-items-center">
          <div className="col-md-5">
            <img
              className="company-logo"
              src={toAbsoluteUrl("/media/logos/print-logo.jpg")}
              width={250}
              alt="Logo"
            />
          </div>
          <div className="col-md-7 text-end">
            <h1 className="company-name-ar">كوزواى لخدمات حزم البضاعع</h1>
            <h2 className="company-name-en">CAUSEWAY CARGO PACKAGING</h2>
            <p className="company-address">
              Warehouse No 5, Building No 6, 5 A Street, Rasal Khor, Industrial
              Area 2
            </p>
            <p className="mt-0 gap-3 d-flex flex-row align-items-center justify-content-end">
              <span className="contact-info">
                <KTSVG
                  path="/media/icons/duotune/electronics/elc002.svg"
                  className="svg-icon-3 me-2"
                />
                +971 52 4268963
              </span>
              <span className="contact-info">
                <KTSVG
                  path="/media/icons/duotune/electronics/elc003.svg"
                  className="svg-icon-3 me-2"
                />
                +971 4 577 6354
              </span>
              <span className="contact-info">
                <KTSVG
                  path="/media/icons/duotune/communication/com011.svg"
                  className="svg-icon-3 me-2"
                />
                sales@causewaycargo.com
              </span>
            </p>
          </div>
        </div>

        {bookingData?.booking_id && (
          <div className="mt-3">
            <table className="consignment-table">
              <tbody>
                <tr>
                  <td colSpan={6} className="consignment-title">
                    CONSIGNMENT NOTE
                  </td>
                </tr>
                <tr>
                  <td colSpan={3} className="field-label">
                    Booking ID :{" "}
                    <span className="field-value">
                      {bookingData.booking_id}
                    </span>
                  </td>
                  <td colSpan={3} className="field-label">
                    Booking Date :{" "}
                    <span className="field-value">
                      {moment(bookingData?.created_at).format("DD MMM YYYY")}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td colSpan={3} className="section-header">
                    Consignor Details
                  </td>
                  <td colSpan={3} className="section-header">
                    Consignee Details
                  </td>
                </tr>
                <tr>
                  <td className="field-label">Name</td>
                  <td colSpan={2} className="field-value">
                    {bookingData.consignor || ""}
                  </td>
                  <td className="field-label">Name</td>
                  <td colSpan={2} className="field-value">
                    {bookingData.consignee || ""}
                  </td>
                </tr>
                <tr>
                  <td className="field-label">Address</td>
                  <td colSpan={2} className="field-value">
                    {bookingData.consignor_address || ""}
                  </td>
                  <td className="field-label">Address</td>
                  <td colSpan={2} className="field-value">
                    {bookingData.consignee_address || ""}
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}></td>
                  <td className="field-label">Note</td>
                  <td colSpan={2} className="field-value">
                    {bookingData.notes || ""}
                  </td>
                </tr>
                <tr>
                  <td colSpan={6} className="section-header">
                    Item Details
                  </td>
                </tr>
                <tr>
                  <td className="field-label">No. of Packages</td>
                  <td colSpan={2} className="field-value">
                    {bookingData.total_quantity || ""}
                  </td>
                  <td className="field-label">Description</td>
                  <td colSpan={2} className="field-value">
                    {bookingData.description || ""}
                  </td>
                </tr>
                <tr>
                  <td className="field-label">Destination</td>
                  <td colSpan={2} className="field-value">
                    {bookingData?.destination_hub || ""}
                  </td>
                  <td className="field-label">Shipment Mode</td>
                  <td colSpan={2} className="field-value">
                    {bookingData.shipment_mode || ""}
                  </td>
                </tr>
                <tr>
                  <td className="field-label">Prepared By</td>
                  <td colSpan={2} className="field-value">
                    {storedName || ""}
                  </td>
                  <td className="field-label">Authorised By</td>
                  <td colSpan={2}></td>
                </tr>
                {bookingData.booking_mode === "split booking" && (
                  <>
                    <tr>
                      <td colSpan={6} className="section-header">
                        Package Breakdown
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <span className="field-label">Large Box:</span>{" "}
                        <span className="field-value">
                          {bookingData.large_box_quantity || 0}
                        </span>
                      </td>
                      <td colSpan={2}>
                        <span className="field-label">Medium Box:</span>{" "}
                        <span className="field-value">
                          {bookingData.medium_box_quantity || 0}
                        </span>
                      </td>
                      <td colSpan={2}>
                        <span className="field-label">Small Box:</span>{" "}
                        <span className="field-value">
                          {bookingData.small_box_quantity || 0}
                        </span>
                      </td>
                    </tr>
                  </>
                )}
                <tr>
                  <td colSpan={3}></td>
                  <td className="field-label">Invoice Value</td>
                  <td colSpan={2} className="field-value">
                    {bookingData.invoice_value || ""}
                  </td>
                </tr>
                <tr>
                  <td colSpan={3}></td>
                  <td className="field-label">Freight Charge</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="row signature-area">
          <div className="col-md-6">
            <h6
              style={{
                fontSize: "clamp(12px, 3.5vw, 14px)",
                fontWeight: "bold",
                marginBottom: "15px",
              }}
            >
              Date: <span>{moment().format("DD MMM YYYY")}</span>
            </h6>
            <h6
              style={{
                fontSize: "clamp(12px, 3.5vw, 14px)",
                fontWeight: "bold",
              }}
            >
              Place:
            </h6>
          </div>
          <div className="col-md-6">
            <h6
              style={{
                fontSize: "clamp(12px, 3.5vw, 14px)",
                fontWeight: "bold",
              }}
            >
              Signature:
            </h6>
          </div>
        </div>
        <div className="footer-text d-flex justify-content-between py-1 px-2 mt-3">
          <p className="mb-0">
            If the received Goods are imitated or forbidden by the Customs, The
            Company will not be liable for the Goods No claims enterteined for
            non insured cargo
          </p>
          <p className="mb-0">Conditions overleaf</p>
        </div>
      </div>
      <div className="d-flex bg-white pb-6 container justify-content-end gap-4 align-items-center mt-3">
        <Link className="btn btn-primary w-auto" to="/bookingtable">
          Cancel
        </Link>
        <button className="btn btn-primary" onClick={generatePdf}>
          Print pdf
        </button>
      </div>
    </>
  );
};

export default BookingPrint;
